import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { apiSyncCredits, getSessionId } from "@/lib/api";

const MAX_POLLS = 15;      // ~30s total
const INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [balance, setBalance] = useState(null);
  const [newCredits, setNewCredits] = useState(0);
  const pollCount = useRef(0);

  useEffect(() => {
    let alive = true;
    let timer;
    const sid = getSessionId();

    const tick = async () => {
      if (!alive) return;
      pollCount.current += 1;
      try {
        const r = await apiSyncCredits(sid);
        if (r && (r.newCreditsGranted > 0 || (r.newPayments && r.newPayments.length))) {
          setBalance(r.balance);
          setNewCredits(r.newCreditsGranted);
          setStatus("paid");
          return;
        }
      } catch {}
      if (pollCount.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(tick, INTERVAL_MS);
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line
  }, []);

  const goHome = () => navigate("/", { replace: true });

  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ background: "#090D16" }}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] glow-violet rounded-full" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] glow-emerald rounded-full" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong p-6 sm:p-10 relative z-10 max-w-md w-full mx-4 text-center"
        data-testid="payment-success-card"
      >
        {status === "polling" && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#10B981] flex items-center justify-center mx-auto mb-5 shadow-[0_0_30px_-4px_#7C3AED]"
            >
              <Sparkles size={24} className="text-white" />
            </motion.div>
            <h1 className="font-display text-2xl font-bold text-ink-900 mb-2">Confirming payment…</h1>
            <p className="text-white/60 text-sm">This only takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 12, stiffness: 260 }}
              className="w-14 h-14 rounded-2xl bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center mx-auto mb-5"
            >
              <CheckCircle2 size={30} className="text-[#10B981]" />
            </motion.div>
            <h1 className="font-display text-3xl font-bold mb-2" style={{ color: "#0F172A" }}>You&apos;re in.</h1>
            <p className="text-ink-500 text-sm mb-6">
              {newCredits > 0 ? `+${newCredits} credits are live in your account.` : "Credits will appear in seconds — refresh if needed."}
            </p>
            <button
              data-testid="payment-success-continue"
              onClick={goHome}
              className="w-full py-3 rounded-xl font-semibold text-sm"
              style={{ backgroundColor: "#059669", color: "#0F172A" }}
            >
              Back to scanning →
            </button>
          </>
        )}
        {["failed", "expired", "refunded", "timeout"].includes(status) && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mx-auto mb-5">
              <XCircle size={30} className="text-[#F59E0B]" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              {status === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-ink-500 text-sm mb-6">
              {status === "timeout"
                ? "Whop is taking longer than usual. You can head back — your credits will appear as soon as Whop confirms."
                : "No charge was made. You can try again anytime."}
            </p>
            <button
              data-testid="payment-success-retry"
              onClick={goHome}
              className="w-full py-3 rounded-xl bg-surface-sunken hover:bg-border-default border border-border-strong font-semibold text-sm"
              style={{ color: "#0F172A" }}
            >
              Back to app
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ background: "#090D16" }}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] glow-violet rounded-full" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong p-6 sm:p-10 relative z-10 max-w-md w-full mx-4 text-center"
        data-testid="payment-cancel-card"
      >
        <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mx-auto mb-5">
          <XCircle size={30} className="text-white/60" />
        </div>
        <h1 className="font-display text-2xl font-bold text-white mb-2">Checkout cancelled</h1>
        <p className="text-white/60 text-sm mb-6">No charge was made. You can pick a plan whenever you&apos;re ready.</p>
        <button
          data-testid="payment-cancel-back"
          onClick={() => navigate("/", { replace: true })}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#6366F1] text-white font-semibold text-sm"
        >
          Back to rsell ai
        </button>
      </motion.div>
    </div>
  );
}
